import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
    ALTERNATIVE,
    BOND,
    COMPANY,
    CUSTOM_SECURITY,
    INDEX,
    INDICATOR,
    MODEL_PORTFOLIO,
    MUTUAL_FUND,
    SEPARATE_ACCOUNT,
} from 'Angular/constants/core.constants';
import { UNDERLYING_HOLDINGS } from 'ModelPortfolioApp/ts/shared/constants';
import { INTERSECT_ALL_HOLDINGS } from 'SecuritiesApp/ts/constants/holdings-table.constants';
import { ContributionMetric } from 'SecuritiesApp/ts/enums/attribution.enums';
import { AttributionMode } from 'SecuritiesApp/ts/features/attribution-factor-analysis/attribution-bar-chart-enums';
import { SecurityType } from 'SecuritiesApp/ts/types/security-type.type';
import { SecurityInfo, SecurityLevelInfo } from '../interfaces/security-info.interface';
import { LooseObject } from 'Angular/interfaces/loose-object.interface';
import { SecurityBenchmark } from 'ModelPortfolioApp/ts/fixed/interfaces/security-benchmark.interface';
import {
    AttributionBenchmarkOption,
    AttributionCategorization,
    AttributionTableGetViewResponse,
    ContributorsDetractorsGetViewResponse,
    FactorAttributionGetViewResponse,
    PerformancePeriodicity,
    RelativeAttributionGetViewResponse,
} from 'SecuritiesApp/ts/interfaces/attribution.interface';
import { HistoricalDataTableResponse } from 'SecuritiesApp/ts/interfaces/historical-data-table.interface';
import {
    CommonHoldingsParams,
    HoldingsResponse,
    SaveHoldingsAsWatchlistResponse,
} from 'SecuritiesApp/ts/interfaces/holdings-table.interface';
import { SerializedRelatedSecurity } from 'SecuritiesApp/ts/interfaces/serialized-related-security.interface';
import {
    TickerTapeSecurityResponse,
    TickerTapeSecurityUpdateRequest,
} from 'SecuritiesApp/ts/interfaces/ticker-tape-securities.interface';
import { EditWatchlistModalSecurity } from 'WatchlistApp/ts/features/edit-watchlist-modal/interfaces/edit-watchlist-modal-security.interface';

import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';

@Injectable({
    providedIn: 'root',
})
export class SecuritiesService {
    constructor(private ajaxService: AjaxService, private urlsService: UrlsService) {}

    getSecuritiesInfo(securityIds: string): Observable<SecurityInfo[]> {
        /**
         * `securityIds` represents a string of comma delimited symbols, e.g. "AAPL,GOOG,TSLA" or just "AAPL"
         */
        const urlParams = { securities: securityIds };
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.SECURITIES_INFO, urlParams);
        return this.ajaxService.getDataObserveBody<SecurityInfo[]>(url);
    }

    getSecuritiesPerformanceProxyInfo(proxyToSecurityMap: {
        [key: string]: string;
    }): Observable<{ [security_id: string]: SecurityBenchmark }> {
        /**
         * `proxyToSecurityMap` represents an object where keys are the
         * performance proxy and the value is the security IDs that the key
         * is proxying. e.g { ^MSACWITR : GOOGL} ^MSACWITR is proxying GOOGL
         */
        const urlParams = { securities: JSON.stringify(proxyToSecurityMap) };
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.SECURITIES_PERFORMANCE_PROXY_INFO, urlParams);
        return this.ajaxService.getDataObserveBody<{ [security_id: string]: SecurityBenchmark }>(url);
    }

    getSecuritiesBenchmarkInfo(securityIds: string): Observable<{ [security_id: string]: SecurityBenchmark }> {
        /**
         * `securityIds` represents a string of comma-delimited symbols, e.g. "AAPL,GOOG,TSLA" or just "AAPL"
         */
        const urlParams = { securities: securityIds };
        const url = this.urlsService.getUrl(
            this.urlsService.SECURITIES.SECURITIES_AUTO_PERFORMANCE_PROXY_INFO,
            urlParams
        );
        return this.ajaxService.getDataObserveBody<{ [security_id: string]: SecurityBenchmark }>(url);
    }

    getSecuritiesLevelsInfo(securityIds: string, performancePeriodicity?: string): Observable<SecurityLevelInfo[]> {
        /**
         * Given a list of security ids return the security level according to the following logic:
         * For model portfolios return the portfolio levels, for funds return the fund price, otherwise the level will
         * be null.
         * `securityIds` represents a string of comma delimited symbols, e.g. "AAPL,GOOG,TSLA" or just "AAPL"
         */
        const queryParams = performancePeriodicity
            ? { securities: securityIds, performancePeriodicity }
            : { securities: securityIds };
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.SECURITIES_LEVEL_INFO, undefined, queryParams);
        return this.ajaxService.getDataObserveBody<SecurityLevelInfo[]>(url);
    }

    /**
     * Given some security IDs, this function retrieves the securities information needed for displaying
     * the securities as part of a watchlist in the edit security watchlist modal.
     * @param securityIds: array of tickers, e.g. ['AAPL', 'GOOG','TSLA']
     * @returns Observable<EditWatchlistModalSecurity[]> with the information for the requested securities
     */
    getSecuritiesInfoForWatchlist(securityIds: string[]): Observable<EditWatchlistModalSecurity[]> {
        const params = { securities: securityIds };
        // We make a POST request because there could be thousands of tickers, and if we pass them as query params in a GET request
        // we will hit the cloudfront URL length limit
        return this.ajaxService.postJsonDataObserveBody<EditWatchlistModalSecurity[]>(
            this.urlsService.SECURITIES.SECURITIES_INFO_WATCHLIST,
            params
        );
    }

    /**
     * Gets list of all valid holding dates for a security via GET request to `/securities/all_holding_dates/{{ securityId }}`.
     *
     * @param securityId: Security ID.
     */
    getAllHoldingDates(securityId: string): Observable<{ holding_dates: string[] }> {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.HOLDING_DATES, { securityId });
        return this.ajaxService.getDataObserveBody<{ holding_dates: string[] }>(url);
    }

    /**
     * Gets All Holdings for a security via GET request to `/securities/all_holdings/{{ securityId }}.json`.
     *
     * @param securityId: Security ID.
     * @param queryParams: object containing additional information for request
     *                     such as holding date, pagination info and sorting info
     */
    getHoldings(securityId: string, queryParams: LooseObject): Observable<HoldingsResponse> {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.HOLDINGS, { securityId }, queryParams);
        return this.ajaxService.getDataObserveBody<HoldingsResponse>(url);
    }

    /**
     * Builds and returns the URL for exporting all holdings' data to a CSV file.
     *
     * @param securityId: Security ID.
     * @param queryParams: optional objects holding sorting and date params for fetching all holdings data
     */
    getSecurityHoldingsExportUrl(securityId: string, queryParams: LooseObject): string {
        return this.urlsService.getUrl(this.urlsService.SECURITIES.HOLDINGS_EXPORT, { securityId }, queryParams);
    }

    getCommonHoldings(primarySecurityId: string, queryParams: CommonHoldingsParams): Observable<HoldingsResponse> {
        const url = this.urlsService.getUrl(
            this.urlsService.SECURITIES.COMMON_HOLDINGS,
            { primarySecurityId },
            queryParams
        );
        return this.ajaxService.getDataObserveBody<HoldingsResponse>(url);
    }

    getCommonHoldingsExportUrl(primarySecurityId: string, queryParams: CommonHoldingsParams): string {
        return this.urlsService.getUrl(
            this.urlsService.SECURITIES.COMMON_HOLDINGS_EXPORT,
            { primarySecurityId },
            queryParams
        );
    }

    /**
     * Makes a request to create a new watchlist based on all the security's holdings.
     *
     * @param securityId: Security ID.
     * @param watchlistName: String with the name for new watchlist.
     * @param useFundTop25: determines whether to create watchlist from M* T25 holdings or lipper All holdings
     * @param holdingDate: optional string holding date to get all holdings data for
     * @param intersectOption - optional string comparable intersect option - all/unique/common
     * @param comparableSecurityIds - optional string[] comparable security ids
     */
    saveHoldingsAsWatchlist(
        securityId: string,
        watchlistName: string,
        useFundTop25: boolean = true,
        holdingDate: string = '',
        intersectOption: string = INTERSECT_ALL_HOLDINGS,
        comparableSecurityIds: string[] = []
    ): Observable<SaveHoldingsAsWatchlistResponse> {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.SAVE_HOLDINGS_AS_WATCHLIST, {
            securityId,
        });
        return this.ajaxService.postFormEncodedDataObserveBody<SaveHoldingsAsWatchlistResponse>(url, {
            watchlistName,
            useFundTop25,
            holdingDate,
            intersectOption,
            comparableSecurityIds,
        });
    }

    /**
     * Gets a FlattenedHoldings object from a GET request to `/securities/flattened_holdings/{{ securityId }}.json`.
     *
     * @param securityId: ID of the model portfolio.
     * @param queryParams: Includes page number, sortBy column, and sort direction.
     */
    getFlattenedHoldings(securityId: string, queryParams: LooseObject): Observable<HoldingsResponse> {
        const url = this.urlsService.getUrl(
            this.urlsService.SECURITIES.FLATTENED_HOLDINGS,
            { securityId },
            queryParams
        );
        return this.ajaxService.getDataObserveBody<HoldingsResponse>(url);
    }

    /**
     * Builds and returns the URL for exporting flattened holdings' data to a CSV file.
     *
     * @param securityId: ID of the model portfolio.
     * @param queryParams: sortBy and sortDirection information
     */
    getSecurityFlattenedHoldingsExportUrl(securityId: string, queryParams: LooseObject): string {
        return this.urlsService.getUrl(
            this.urlsService.SECURITIES.FLATTENED_HOLDINGS_EXPORT,
            { securityId },
            queryParams
        );
    }

    /**
     * Builds and returns the URL for exporting flattened holdings' data to a PDF file.
     *
     * @param securityId: ID of the model portfolio.
     */
    getExpandedHoldingsPDFReportUrl(securityId: string): string {
        return this.urlsService.getUrl(this.urlsService.SECURITIES.EXPANDED_HOLDINGS_PDF_REPORT, { securityId });
    }

    /**
     * Makes a request to create a new watchlist based on the model portfolio's flattened holdings.
     *
     * @param securityId: ID of the model portfolio.
     * @param watchlistName: string with the name for new watchlist.
     * @param intersectOption - optional string comparable intersect option - all/unique/common
     * @param comparableSecurityIds - optional string[] comparable security ids
     */
    saveFlattenedHoldingsAsWatchlist(
        securityId: string,
        watchlistName: string,
        intersectOption: string = INTERSECT_ALL_HOLDINGS,
        comparableSecurityIds: string[] = [],
        holdingsLayer: string = UNDERLYING_HOLDINGS
    ): Observable<SaveHoldingsAsWatchlistResponse> {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.SAVE_FLATTENED_HOLDINGS_AS_WATCHLIST, {
            securityId,
        });
        return this.ajaxService.postFormEncodedDataObserveBody<SaveHoldingsAsWatchlistResponse>(url, {
            watchlistName,
            intersectOption,
            comparableSecurityIds,
            holdingsLayer,
        });
    }

    /**
     * Make a request to retrieve security data
     *
     * @param securityType: specified security type
     * @param urlParams: url params
     * @param getParams: GET query params
     */
    getSecurityDataTable(
        securityType: SecurityType,
        urlParams: Record<string, string | number>,
        getParams: Record<string, string | number>
    ): Observable<HistoricalDataTableResponse> {
        let urlBase: string;
        switch (securityType) {
            case COMPANY:
                urlBase = this.urlsService.HISTORICAL_DATA.COMPANY_TABLE;
                break;
            case CUSTOM_SECURITY:
                urlBase = this.urlsService.HISTORICAL_DATA.CUSTOM_SECURITY_TABLE;
                break;
            case INDEX:
                urlBase = this.urlsService.HISTORICAL_DATA.INDEX_TABLE;
                break;
            case INDICATOR:
                urlBase = this.urlsService.HISTORICAL_DATA.INDICATOR_TABLE;
                break;
            case MUTUAL_FUND:
                urlBase = this.urlsService.HISTORICAL_DATA.MUTUAL_FUND_TABLE;
                break;
            case MODEL_PORTFOLIO:
                urlBase = this.urlsService.HISTORICAL_DATA.MODEL_PORTFOLIO_TABLE;
                break;
            case SEPARATE_ACCOUNT:
                urlBase = this.urlsService.HISTORICAL_DATA.SEPARATE_ACCOUNT_TABLE;
                break;
            case BOND:
                urlBase = this.urlsService.HISTORICAL_DATA.BOND_TABLE;
                break;
            case ALTERNATIVE:
                urlBase = this.urlsService.HISTORICAL_DATA.ALTERNATIVE_TABLE;
                break;
            default:
                throw new Error(`${securityType} is not a valid security type for this function.`);
        }
        const url = this.urlsService.getUrl(urlBase, urlParams);
        return this.ajaxService.getDataObserveBody<HistoricalDataTableResponse>(url, getParams);
    }

    /**
     * Retrieve an url to download historical data
     * @param securityType: specified security type
     * @param urlParams: url param
     */
    getSecurityDataTableExportUrl(securityType: SecurityType, urlParams: Record<string, string | number>): string {
        let urlBase;
        switch (securityType) {
            case BOND:
                urlBase = this.urlsService.HISTORICAL_DATA.BOND_EXPORT;
                break;
            case COMPANY:
                urlBase = this.urlsService.HISTORICAL_DATA.COMPANY_EXPORT;
                break;
            case CUSTOM_SECURITY:
                urlBase = this.urlsService.HISTORICAL_DATA.CUSTOM_SECURITY_EXPORT;
                break;
            case INDEX:
                urlBase = this.urlsService.HISTORICAL_DATA.INDEX_EXPORT;
                break;
            case INDICATOR:
                urlBase = this.urlsService.HISTORICAL_DATA.INDICATOR_EXPORT;
                break;
            case MUTUAL_FUND:
                urlBase = this.urlsService.HISTORICAL_DATA.MUTUAL_FUND_EXPORT;
                break;
            case MODEL_PORTFOLIO:
                urlBase = this.urlsService.HISTORICAL_DATA.MODEL_PORTFOLIO_EXPORT;
                break;
            case SEPARATE_ACCOUNT:
                urlBase = this.urlsService.HISTORICAL_DATA.SEPARATE_ACCOUNT_EXPORT;
                break;
            case ALTERNATIVE:
                urlBase = this.urlsService.HISTORICAL_DATA.ALTERNATIVE_EXPORT;
                break;
            default:
                throw new Error(`${securityType} is not a valid security type for this function.`);
        }
        return this.urlsService.getUrl(urlBase, urlParams);
    }

    /**
     * Returns a list of related securities for a security.
     *
     * @param securityId: Security ID.
     */
    getRelatedSecurities(securityId: string): Observable<SerializedRelatedSecurity[]> {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.DEFAULT_RELATED_SECURITIES, {
            securityId,
        });
        return this.ajaxService.getDataObserveBody<SerializedRelatedSecurity[]>(url);
    }

    /**
     * Returns a list of related securities set by the user for a security.
     *
     * @param securityId
     * @param returnDefault
     */
    getUserRelatedSecurities(
        securityId: string,
        returnDefault: boolean = false
    ): Observable<SerializedRelatedSecurity[]> {
        const queryParams = { returnDefault };
        const url = this.urlsService.getUrl(
            this.urlsService.SECURITIES.USER_RELATED_SECURITY_COLLECTION,
            {
                securityId,
            },
            queryParams
        );
        return this.ajaxService.getDataObserveBody<SerializedRelatedSecurity[]>(url);
    }

    getUserRelatedHoldings(securityId: string): Observable<SerializedRelatedSecurity[]> {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.USER_RELATED_HOLDING_COLLECTION, {
            securityId,
        });
        return this.ajaxService.getDataObserveBody<SerializedRelatedSecurity[]>(url);
    }

    /**
     * Updates a user's related securities list for a security.
     *
     * @param securityId: Security ID.
     * @param relatedSecurityIds: List of related securities IDs.
     */
    putUserRelatedSecurities(securityId: string, relatedSecurityIds: string[]): Observable<unknown> {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.USER_RELATED_SECURITY_COLLECTION, {
            securityId,
        });
        return this.ajaxService.putDataObserveBody(url, { relatedSecurityIds });
    }

    putUserRelatedHoldings(securityId: string, relatedHoldingIds: string[]): Observable<unknown> {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.USER_RELATED_HOLDING_COLLECTION, {
            securityId,
        });
        return this.ajaxService.putDataObserveBody(url, { relatedHoldingIds });
    }

    /**
     * Gets Ticker Tape security information either from specified request or from User Preferences`.
     *
     * @param tickerTapeSecurities: object with selected securities or selected security list
     */
    getTickerTapeSecurities(
        tickerTapeSecurities?: TickerTapeSecurityUpdateRequest
    ): Observable<TickerTapeSecurityResponse> {
        let request = {};
        if (tickerTapeSecurities) {
            request = { ...(tickerTapeSecurities.selected_securitylist || tickerTapeSecurities) };
        }
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.TICKER_TAPE_SECURITIES, {}, request);
        return this.ajaxService.getDataObserveBody<TickerTapeSecurityResponse>(url);
    }

    /**
     * Returns data to populate a chart of top contributors and detractors to a security's overall performance.
     *
     * @param securityId: Security ID.
     * @param performancePeriodicity: Periodicity for which to fetch performance data
     */
    getContributorAndDetractorInfo(
        securityId: string,
        performancePeriodicity: PerformancePeriodicity,
        holdingsLayer: string,
        asOfDate: string,
        asOfDateCustom: string
    ): Observable<ContributorsDetractorsGetViewResponse> {
        const url = this.urlsService.getUrl(
            this.urlsService.SECURITIES.ATTRIBUTION_CONTRIBUTORS_DETRACTORS,
            { securityId },
            { performancePeriodicity, holdingsLayer, asOfDate, asOfDateCustom }
        );
        return this.ajaxService.getDataObserveBody<ContributorsDetractorsGetViewResponse>(url);
    }

    /**
     * Returns data needed to populate a security's Attribution table (absolute).
     *
     * @param securityId: Security ID
     * @param page: Page number
     * @param sortColumn: Column to sort by
     * @param sortDirection: Direction of the sort
     * @param metric: Metric used to calculate contribution
     */
    getAttributionTableData(
        securityId: string,
        page: number,
        sortColumn: string,
        sortDirection: string,
        metric: ContributionMetric,
        holdingsLayer: string,
        asOfDate: string,
        asOfDateCustom: string
    ): Observable<AttributionTableGetViewResponse> {
        const url = this.urlsService.getUrl(
            this.urlsService.SECURITIES.ATTRIBUTION_TABLE_DATA,
            { securityId },
            { page, sortColumn, sortDirection, metric, holdingsLayer, asOfDate, asOfDateCustom }
        );
        return this.ajaxService.getDataObserveBody<AttributionTableGetViewResponse>(url);
    }

    /**
     * Returns file (CSV) of data that populates the security's Attribution table (absolute).
     *
     * @param securityId: Security ID
     * @param format: File format (csv)
     * @param sortColumn: Column to sort by
     * @param sortDirection: Direction of the sort
     * @param metric: Metric used to calculate contribution
     * @param title: Title of the file
     */
    getAttributionTableDataExport(
        securityId: string,
        format: string,
        sortColumn: string,
        sortDirection: string,
        metric: ContributionMetric,
        holdingsLayer: string,
        asOfDate: string,
        asOfDateCustom: string,
        title: string | null = null
    ): Observable<any> {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.ATTRIBUTION_TABLE_DATA_EXPORT, {
            securityId,
            format,
        });

        if (format === 'csv') {
            return this.ajaxService.postFormEncodedDataWithTextObserveResponse(url, {
                sortColumn,
                sortDirection,
                metric,
                holdingsLayer,
                asOfDate,
                asOfDateCustom,
            });
        }
        return this.ajaxService.postFormEncodedDataObserveBody(url, {
            sortColumn,
            sortDirection,
            title,
            metric,
            holdingsLayer,
            asOfDate,
            asOfDateCustom,
        });
    }

    /**
     * Returns relative attribution data for an aggregate security.
     *
     * @param securityId: Security ID.
     * @param performancePeriodicity: Periodicity for which to fetch performance data
     */
    getRelativeAttributionData(
        securityId: string,
        performancePeriodicity: PerformancePeriodicity,
        benchmarkOption: AttributionBenchmarkOption,
        customBenchmarkId: string | null,
        categorization: AttributionCategorization,
        asOfDate: string,
        asOfDateCustom: string
    ): Observable<RelativeAttributionGetViewResponse> {
        const url = this.urlsService.getUrl(
            this.urlsService.SECURITIES.RELATIVE_ATTRIBUTION,
            { securityId },
            { performancePeriodicity, benchmarkOption, customBenchmarkId, categorization, asOfDate, asOfDateCustom }
        );
        return this.ajaxService.getDataObserveBody<RelativeAttributionGetViewResponse>(url);
    }

    /**
     * Returns relative attribution data pdf export for an aggregate security.
     *
     * @param securityId: Security ID.
     * @param performancePeriodicity: Periodicity for which to fetch performance data
     */
    getRelativeAttributionDataExport(
        securityId: string,
        title: string,
        performancePeriodicity: PerformancePeriodicity,
        benchmarkOption: AttributionBenchmarkOption,
        customBenchmarkId: string | null,
        categorization: AttributionCategorization,
        asOfDate: string,
        asOfDateCustom: string
    ): Observable<{ pdf_url: string }> {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.RELATIVE_ATTRIBUTION_EXPORT, { securityId });
        return this.ajaxService.postFormEncodedDataObserveBody(url, {
            performancePeriodicity,
            benchmarkOption,
            customBenchmarkId,
            categorization,
            asOfDate,
            asOfDateCustom,
            title,
        });
    }

    /**
     * Returns Factor Attribution data for an aggregate security.
     */
    getFactorAttributionData(
        securityId: string,
        performancePeriodicity: PerformancePeriodicity,
        attributionMode: AttributionMode
    ): Observable<FactorAttributionGetViewResponse> {
        const url = this.urlsService.getUrl(
            this.urlsService.SECURITIES.FACTOR_ATTRIBUTION,
            { securityId },
            { performancePeriodicity, attributionMode }
        );
        return this.ajaxService.getDataObserveBody<FactorAttributionGetViewResponse>(url);
    }

    getFactorAttributionDataExport(
        securityId: string,
        title: string,
        performancePeriodicity: PerformancePeriodicity,
        attributionMode: AttributionMode
    ): Observable<{ pdf_url: string }> {
        const url = this.urlsService.getUrl(this.urlsService.SECURITIES.FACTOR_ATTRIBUTION_EXPORT, { securityId });
        return this.ajaxService.postFormEncodedDataObserveBody(url, { performancePeriodicity, attributionMode, title });
    }
}
